// Contract-WidgetEmailOTP.md (FROZEN, 2026-07-20) — email OTP verification.
//
// Why this exists: the widget embeds on external, non-Shopify sites where no
// storefront login cookie exists, so `shopify_get_orders` and friends were
// scoped only by an email the visitor typed. This modal proves the visitor
// owns that email before the server will return any order/customer data.
//
// 🚨 The OTP does NOT flow through the agent. The agent never asks for, sees,
// or handles the code. This modal opens on the `verification_required` FLAG in
// the chat response — never on agent text and never on a message marker.
// Product data stays public; only customer/order tools are gated.
//
// 🚨 Never reveal whether an email exists. `/verify/request` always answers
// 200 {sent: true} — unknown email, rate-limited, or SES failure alike — and
// the copy below is identical in every case ("If that email has orders with
// us..."). Branching this copy would rebuild the enumeration oracle the
// contract exists to prevent.
//
// Rendered at the Widget.tsx parent level so it overlays BOTH the chat and
// voice surfaces — the same pattern CSATModal uses. During a voice session the
// customer types the code with the text input here; codes are never read
// aloud and spoken entry is never expected.
//
// Two internal steps, one modal, no navigation:
//   email → "Send code" → POST /verify/request
//   code  → "Verify"    → POST /verify/confirm → onVerified()
//
// Responsive: 375px → full-width card with 16px gutters. Dark mode matches
// the CSAT/maintenance card palette.

import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2, Mail, ShieldCheck } from 'lucide-react';
import { widgetAPI } from '../../services/api';

interface EmailVerificationModalProps {
  sessionId: string;
  agentId: string;
  brandColor: string;
  theme: 'light' | 'dark';
  /** Called once the server confirms the code. The parent closes the modal and
   *  re-sends the question that tripped the gate. */
  onVerified: (email: string) => void;
  onClose: () => void;
  /** Prefill for the email field. The parent passes the address the visitor
   *  already typed into the CHAT box — the "answered in the wrong place" path.
   *  The agent asks for the email in prose and this dialog asks for it in a
   *  form, so a visitor who replies in chat has already answered; making them
   *  retype it into the modal is the desync the visitor actually feels. */
  initialEmail?: string;
  /** The address this session already confirmed, if any. Local FE state, used
   *  only to name it back to the visitor. */
  verifiedEmail?: string | null;
  /** `verification_reason` off the WS frame. 🚨 The two values need OPPOSITE
   *  copy and conflating them is a hard infinite loop (Contract §reason codes):
   *
   *  - `order_lookup`  → nothing confirmed yet, OR the 30-min window lapsed.
   *                      The escape is to verify THIS address.
   *  - `different_email` → the visitor IS verified; the order simply is not
   *                      under the address they confirmed. The escape is to
   *                      verify ANOTHER address — re-entering the same one can
   *                      never satisfy this branch, which is the loop.
   *
   *  🚨 This reason is safe to show only because the backend collapses a
   *  missing order into the SAME `order_email_mismatch` as a stranger's order.
   *  So the copy below must read identically whether or not the order is real —
   *  never assert that the order exists, never hint at whose it is. The
   *  `/verify/request` copy stays identical in every case regardless. */
  reason?: 'order_lookup' | 'different_email';
}

type Step = 'email' | 'code';

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({
  sessionId,
  agentId,
  brandColor,
  theme,
  onVerified,
  onClose,
  initialEmail = '',
  verifiedEmail = null,
  reason = 'order_lookup',
}) => {
  // The order is under an address other than the confirmed one. Distinct from
  // "re-confirm the same address", which is what a lapsed window needs.
  const needsDifferentEmail = reason === 'different_email';
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Shown after a successful send. Deliberately non-committal about whether
  // the address exists (see header).
  const [sentNotice, setSentNotice] = useState(false);
  // Set when the code is spent (too many attempts / expired): the code input
  // is useless now, so push the user back to requesting a fresh one.
  const [mustRestart, setMustRestart] = useState(false);

  const isDark = theme === 'dark';
  const emailInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Focus the field belonging to the current step. A prefilled address is
  // selected rather than just focused: on the reopen path the visitor's next
  // action is to REPLACE it, and a caret parked at the end of an address they
  // already tried is the slowest possible way to do that.
  useEffect(() => {
    const el = step === 'email' ? emailInputRef.current : codeInputRef.current;
    el?.focus();
    if (step === 'email' && el && (el as HTMLInputElement).value) {
      (el as HTMLInputElement).select();
    }
  }, [step]);

  // Esc closes. The gate is server-side, so dismissing costs nothing — the
  // tool simply keeps returning no data until the visitor verifies.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleSendCode = async () => {
    if (!emailLooksValid || busy) return;
    setBusy(true);
    setError(null);
    try {
      await widgetAPI.verifyRequest(sessionId, agentId, email.trim());
      // The response is always {sent: true} — nothing here is conditional on
      // it, by design.
      setSentNotice(true);
      setMustRestart(false);
      setCode('');
      setStep('code');
    } catch {
      // A transport failure is not an enumeration signal, so it's safe to
      // surface it plainly.
      setError("We couldn't send the code just now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = async () => {
    if (code.trim().length !== 6 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await widgetAPI.verifyConfirm(sessionId, email.trim(), code.trim());
      if (res.data.verified) {
        onVerified(email.trim());
        return;
      }
      switch (res.data.reason) {
        case 'too_many_attempts':
          setError('Too many incorrect attempts. Request a new code.');
          setMustRestart(true);
          break;
        case 'expired':
          setError('That code has expired. Request a new code.');
          setMustRestart(true);
          break;
        default:
          setError("That code doesn't match. Check it and try again.");
      }
    } catch {
      setError("We couldn't check that code just now. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const backToEmail = () => {
    setStep('email');
    setCode('');
    setError(null);
    setSentNotice(false);
    setMustRestart(false);
  };

  const cardBg = isDark
    ? 'bg-gray-900 border-gray-800 text-gray-100'
    : 'bg-white border-gray-200 text-gray-800';
  const subtleText = isDark ? 'text-gray-400' : 'text-gray-500';
  const inputClasses = isDark
    ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400';

  const primaryBtn =
    'w-full px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="widget-verify-heading"
    >
      <div
        className={`relative w-full max-w-sm rounded-2xl shadow-2xl border p-5 sm:p-6 ${cardBg}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className={`absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 rounded-full hover:opacity-80 ${
            isDark ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-400 hover:bg-gray-100'
          }`}
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-2.5 mb-1 pr-8">
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-xl flex-shrink-0"
            style={{ backgroundColor: `${brandColor}1a`, color: brandColor }}
          >
            {step === 'email' ? (
              <Mail className="w-4 h-4" />
            ) : (
              <ShieldCheck className="w-4 h-4" />
            )}
          </span>
          <h3 id="widget-verify-heading" className="text-lg font-semibold">
            {step === 'code'
              ? 'Enter your code'
              : needsDifferentEmail
                ? 'Try a different email'
                : verifiedEmail
                  ? 'Confirm your email again'
                  : 'Verify your email'}
          </h3>
        </div>

        {step === 'email' && (
          <div className="space-y-4">
            {/* Reopened after a completed verification ⇒ the address we
                confirmed is not the one on the order they're asking about.
                Repeating the generic "confirm it's you" line here is what
                traps them: they re-enter the same address and get the same
                dialog back. Local state only — see `verifiedEmail` above. */}
            {needsDifferentEmail ? (
              /* 🚨 The confirmed address stays confirmed — say so, or this
                 reads as the earlier verification having failed and the
                 visitor retypes it, which is exactly the loop. Invite a
                 DIFFERENT address and give them the reasons an order legitimately
                 carries one, so the ask doesn't feel like an accusation.

                 🚨 Never assert the order exists: the backend returns this same
                 reason for a stranger's order, an order with no email, and an
                 order that does not exist at all. "We couldn't open it with
                 that address" is true in all three. */
              <p className={`text-sm leading-relaxed ${subtleText}`}>
                {verifiedEmail ? (
                  <>
                    <span className="font-medium break-words">
                      {verifiedEmail}
                    </span>{' '}
                    is confirmed
                  </>
                ) : (
                  'Your email is confirmed'
                )}
                {' '}— but we couldn't open that order with it. Orders placed as
                a gift, through guest checkout, or under an older address use a
                different email. Enter that one and we'll send a fresh code.
              </p>
            ) : verifiedEmail ? (
              /* Same reason code as a first-time visitor (`order_lookup`) but we
                 already confirmed an address this session ⇒ the 30-minute
                 window lapsed. Here re-entering the SAME address is correct, so
                 the parent keeps it prefilled. */
              <p className={`text-sm leading-relaxed ${subtleText}`}>
                Your confirmation of{' '}
                <span className="font-medium break-words">{verifiedEmail}</span>{' '}
                has expired. Send a fresh code to that address to pick up where
                you left off.
              </p>
            ) : (
              <p className={`text-sm leading-relaxed ${subtleText}`}>
                To look up your order details we need to confirm it's you. Enter
                the email address you used for your order and we'll send you a
                6-digit code.
              </p>
            )}

            <div>
              <label
                htmlFor="widget-verify-email"
                className="block text-sm font-medium mb-1.5"
              >
                Email address
              </label>
              <input
                id="widget-verify-email"
                ref={emailInputRef}
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleSendCode();
                }}
                placeholder="you@example.com"
                className={`w-full px-3 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-offset-0 ${inputClasses}`}
                style={{ outlineColor: brandColor }}
              />
            </div>

            {error && (
              <p className="text-sm text-red-500 dark:text-red-400" role="alert">
                {error}
              </p>
            )}

            <button
              type="button"
              onClick={handleSendCode}
              disabled={!emailLooksValid || busy}
              className={primaryBtn}
              style={{ backgroundColor: brandColor }}
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {busy ? 'Sending…' : 'Send code'}
            </button>
          </div>
        )}

        {step === 'code' && (
          <div className="space-y-4">
            {/* 🚨 Identical wording for every outcome — existing address,
                unknown address, rate-limited, or send failure. */}
            {sentNotice && (
              <p className={`text-sm leading-relaxed ${subtleText}`}>
                If that email has orders with us, we've sent a code. Enter it
                below — it expires in 10 minutes.
              </p>
            )}

            <div>
              <label
                htmlFor="widget-verify-code"
                className="block text-sm font-medium mb-1.5"
              >
                6-digit code
              </label>
              <input
                id="widget-verify-code"
                ref={codeInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleConfirm();
                }}
                placeholder="123456"
                disabled={mustRestart}
                className={`w-full px-3 py-2.5 rounded-xl border text-center text-lg font-semibold tracking-[0.4em] focus:outline-none focus:ring-2 disabled:opacity-50 ${inputClasses}`}
              />
            </div>

            {error && (
              <p className="text-sm text-red-500 dark:text-red-400" role="alert">
                {error}
              </p>
            )}

            {!mustRestart && (
              <button
                type="button"
                onClick={handleConfirm}
                disabled={code.length !== 6 || busy}
                className={primaryBtn}
                style={{ backgroundColor: brandColor }}
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {busy ? 'Verifying…' : 'Verify'}
              </button>
            )}

            <button
              type="button"
              onClick={mustRestart ? backToEmail : handleSendCode}
              disabled={busy}
              className={`w-full text-sm font-medium hover:underline disabled:opacity-50 ${subtleText}`}
            >
              {mustRestart ? 'Request a new code' : 'Use a different email'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailVerificationModal;
